import * as braze from '@braze/web-sdk';
import HttpService from 'services/HttpServiceBasic';
import UserStatus from 'models/UserStatus';
import BrazeConstants from 'models/BrazeConstants';
import { UserPackageSource } from '../models/UserPackage';

const PROFILE_MANAGER_BASE_PATH = '/user-profile/';

export default class Braze {
    sdkEndpoint = CBS.Registry.Braze.env.braze_sdk_endpoint;
    apiKey = CBS.Registry.Braze.env.braze_api_key;
    safariPushId = CBS.Registry.Braze.env.braze_safari_push_id;
    http = null;
    logging = CBS.Registry.Braze.env.braze_logging_enabled;
    initialized = false;
    onInit = [];

    constructor() {
        this.http = new HttpService();

        this.isUserProfileQualified() ? this.init(CBS.Registry.user.profile_id) : braze.destroy();
    }

    init(currentProfileId) {
        braze.initialize(this.apiKey, {
            baseUrl: this.sdkEndpoint,
            enableLogging: this.logging,
            serviceWorkerLocation: '/sw.js',
            safariWebsitePushId: this.safariPushId,
            allowUserSuppliedJavascript: true,
            inAppMessageZIndex: 100050,
            doNotLoadFontAwesome: true,
        });

        // Register profile and opens session
        try {
            braze.getUser().getUserId((profileId) => {
                if (profileId === String(currentProfileId)) {
                    this.openBrazeSession();
                } else {
                    this.registerUserProfile();
                }
            });
        } catch (e) {
            console.log('Unable to init Braze user', e);
        }

        // Add Braze session to CBS object
        CBS.Braze = { brazeObj: this, braze: braze };
    }

    registerUserProfile() {
        this.getUserProfile()
            .then((response) => {
                if (response && response['activeProfile']) {
                    let activeProfile = response['activeProfile'];

                    braze.changeUser(activeProfile.id);
                    let user = braze.getUser();

                    user.setFirstName(activeProfile.id.toString());

                    // Set custom user attributes
                    Object.keys(this.getProfileAttributes(activeProfile)).map((key) => {
                        user.setCustomUserAttribute(
                            key,
                            this.getProfileAttributes(activeProfile)[key],
                        );
                    });

                    user.setPushNotificationSubscriptionType(
                        braze.User.NotificationSubscriptionTypes.SUBSCRIBED,
                    );

                    this.openBrazeSession();
                }
            })
            .catch((err) => {
                console.error(`Get User Profile Error: ${err.message}`);
            });
    }

    openBrazeSession() {
        braze.automaticallyShowInAppMessages();
        braze.openSession();

        // Braze session is initialized
        this.initialized = true;
        for (let i = 0; i <= this.onInit.length; i++) {
            if (typeof this.onInit[i] === 'function') {
                this.onInit[i]();
            }
        }
    }

    getUserProfile() {
        const url = '/user-profile/xhr/user/';
        return this.http.doGet(url);
    }

    isInitialized() {
        return this.initialized;
    }

    setOnInit(callback = function () {}) {
        this.onInit.push(callback);
    }

    getProfileAttributes(activeProfile) {
        const activeProfileType = activeProfile.profileType;
        const activeProfilePicPath = activeProfile.profilePicPath;
        const plan = CBS.Registry.user.plan;
        const userMVPDAuthz = CBS.Registry.user.isMVPDAuthZ ?? false;
        const subStatus = CBS.Registry.user.sub_status;
        const showtimeBundle = CBS.Registry.user.hasShowtimeAddon ?? false;
        const brandPlatformId = CBS.Registry.brandPlatformID;
        const userStateRecurly = CBS.Registry.user.source === UserPackageSource.SOURCE_RECURLY;
        const USER_STATUS = {
            an: UserStatus.STATUS_ANONYMOUS,
            sb: UserStatus.STATUS_SUBSCRIBER,
            rg: UserStatus.STATUS_REGISTERED,
            esb: UserStatus.STATUS_EX_SUBSCRIBER,
        };

        return {
            profileType: activeProfileType,
            'Plan - CF Annual': plan === BrazeConstants.PLAN_TYPES.cf['0'],
            'Plan - CF Monthly': plan === BrazeConstants.PLAN_TYPES.cf['1'],
            'Plan - LC Annual': plan === BrazeConstants.PLAN_TYPES.lc['0'],
            'Plan - LC Monthly': plan === BrazeConstants.PLAN_TYPES.lc['1'],
            'Plan - LCP Annual': plan === BrazeConstants.PLAN_TYPES.lcp['0'],
            'Plan - LCP Monthly': plan === BrazeConstants.PLAN_TYPES.lcp['1'],
            'User State - MVPD_AUTHZ': userMVPDAuthz,
            'User State - Ex Subscriber': subStatus === USER_STATUS.esb,
            'User State - Registered': subStatus === USER_STATUS.rg,
            'User State - Subscriber': subStatus === USER_STATUS.sb,
            BrandPlatformID: brandPlatformId,
            'showtime bundle': showtimeBundle,
            'User State - Recurly': userStateRecurly,
            AvatarImageUrl: activeProfilePicPath,
        };
    }

    isUserProfileQualified() {
        return (
            CBS.Registry.Features.braze_sdk === true &&
            CBS.Registry.user_auth &&
            CBS.Registry.user.profile_type === 'ADULT' &&
            !window.location.href.includes('nickeldodeon') &&
            !window.location.href.includes('#kids')
        );
    }

    /**
     * Request Push Notification Permission
     * if browser supports notifications
     */
    requestBrowserNotificationPermission() {
        return new Promise((resolve, reject) => {
            if (braze.isPushSupported() && !braze.isPushPermissionGranted()) {
                if (typeof Notification !== 'undefined') {
                    let user = braze.getUser();
                    if (user) {
                        user.setPushNotificationSubscriptionType(
                            braze.User.NotificationSubscriptionTypes.SUBSCRIBED,
                        );
                    }
                    resolve(Notification.requestPermission());
                } else {
                    reject('This Browser does not support Notifications');
                }
            } else if (!braze.isPushSupported()) {
                reject('Notifications not supported');
            } else {
                resolve('Notifications are already granted');
            }
        });
    }

    testIAM() {
        return this.logging && this.initialized
            ? braze.logCustomEvent('page_view', { name: 'testIAM' })
            : false;
    }
}
